import React from 'react';
import { DRAFT_COSTS } from '../../utils/gameRules';
import { Inventory, PieceType } from '../../types';
import { Coins, Shield } from 'lucide-react';

interface PieceSelectorProps {
  inventory: Inventory;
  onChange: (type: PieceType, delta: number) => void;
  resources: number;
}

const PIECE_LABELS: Record<PieceType, string> = {
  p: 'Pawn',
  n: 'Knight',
  b: 'Bishop',
  r: 'Rook',
  q: 'Queen',
  k: 'King'
};

const PIECE_ICONS: Record<PieceType, string> = {
  p: '♟',
  n: '♞',
  b: '♝',
  r: '♜',
  q: '♛',
  k: '♚'
};

export const PieceSelector: React.FC<PieceSelectorProps> = ({ inventory, onChange, resources }) => {
  const pieces: PieceType[] = ['p', 'n', 'b', 'r', 'q', 'k'];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {pieces.map((type) => {
        const cost = DRAFT_COSTS[type];
        const count = inventory[type];
        // King/Queen are unique (max 1), others unlimited
        const isMaxed = (type === 'k' || type === 'q') && count >= 1;
        const canAfford = resources >= cost;

        return (
          <div
            key={type}
            className={`border border-tudor-gray bg-tudor-dark p-4 flex flex-col items-center justify-between rounded hover:border-tudor-gold transition-colors ${isMaxed ? 'opacity-50' : ''}`}
          >
            <div className="text-4xl mb-2 text-tudor-text">{PIECE_ICONS[type]}</div>
            <div className="text-sm font-bold uppercase tracking-wider mb-1">{PIECE_LABELS[type]}</div>
            <div className="text-xs text-tudor-gold mb-3 flex items-center">
              <Coins size={12} className="mr-1" />
              {cost === 0 ? 'FREE' : cost}
            </div>

            <div className="flex items-center space-x-3 w-full justify-center">
              <button
                onClick={() => onChange(type, -1)}
                disabled={count === 0 || (type === 'k' && count === 1)} // Cannot remove mandatory King? Actually prompt says mandatory.
                className="w-8 h-8 flex items-center justify-center bg-tudor-gray rounded text-tudor-text hover:bg-red-900 disabled:opacity-30"
              >
                -
              </button>
              <span className="font-mono text-lg w-6 text-center">{count}</span>
              <button
                onClick={() => onChange(type, 1)}
                disabled={isMaxed || (!canAfford && cost > 0)}
                className="w-8 h-8 flex items-center justify-center bg-tudor-gray rounded text-tudor-text hover:bg-green-900 disabled:opacity-30"
              >
                +
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
